import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getApiUrl } from '../config/api';
import { authUtils } from '../utils/authUtils';
import CosmicBackground from '../components/CosmicBackground';
import { Loader2, Sparkles, AlertTriangle } from 'lucide-react';

// Module-scoped cache of in-flight (or completed) join promises, keyed by
// invite token. Both React 18 StrictMode-invoked effect runs await the SAME
// promise: exactly ONE POST /api/agent-guest/join fires, and both mounts
// apply the result via setState (React silently discards setState on the
// unmounted first instance; the remounted mounted instance applies it and
// completes the join flow). This is the only structure that survives all
// of: StrictMode simulated remount, HMR re-evaluation, and double-mount —
// where useRef, module-Sets, and sessionStorage flags all failed in
// iterations 44/45/46 (see /app/test_reports/).
const joinPromises = new Map();

// Agent Guest join landing page.
// The steward gives their agent a URL like https://alvins.place/agent-guest/join/<token>.
// The agent visits it; this page POSTs the token to /api/agent-guest/join, receives
// a session token, and stores it in the same localStorage keys the rest of the app
// reads from — so from that point forward the agent is signed into the steward's
// realm with a distinct guest identity.
export default function AgentGuestJoin() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('joining'); // joining | ok | error
  const [guestName, setGuestName] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    if (!token) { setStatus('error'); setErrorMsg('No invite token in URL'); return; }

    // Deduplicate the join POST across StrictMode's double-effect: both
    // effect invocations share the same cached Promise, so exactly one
    // network call happens but both mounts get to apply the result.
    if (!joinPromises.has(token)) {
      joinPromises.set(token, (async () => {
        const res = await fetch(`${getApiUrl()}/api/agent-guest/join`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        const data = await res.json();
        if (!res.ok) {
          const err = new Error(data.detail || 'Could not join');
          err.__detail = data.detail;
          throw err;
        }
        return data;
      })());
    }

    let cancelled = false;
    (async () => {
      try {
        const data = await joinPromises.get(token);
        if (cancelled) return;
        // Use authUtils.setToken so we write to the SAME localStorage key
        // (`alvins_session_token`) that the rest of the app reads from —
        // otherwise auth checks on the next page redirect to Google OAuth
        // even though the guest was successfully authenticated.
        try {
          authUtils.setToken(data.session_token);
          localStorage.setItem('is_guest_session', 'true');
          localStorage.setItem('guest_name', data.guest_name);
        } catch (_) { /* private-mode fallback: session lives in memory only */ }
        setGuestName(data.guest_name);
        setStatus('ok');

        // Aren's Feb 2026 room-binding rule: if the invitation itself
        // carried a bound vessel_id + conversation_id, use those. That is
        // the authoritative destination — the steward explicitly said
        // "come into THIS room." Only fall back to the my-conversations
        // search when the invitation was not bound (legacy invites).
        let targetVesselId = data.vessel_id || null;
        let targetConvId = data.conversation_id || null;

        if (!targetVesselId || !targetConvId) {
          // Legacy fallback: search for any conversation this guest has
          // already spoken in. Note this fails on FIRST visit (no history).
          try {
            const convRes = await fetch(
              `${getApiUrl()}/api/agent-guest/my-conversations`,
              { headers: { 'Authorization': `Bearer ${data.session_token}` } }
            );
            if (convRes.ok) {
              const convData = await convRes.json();
              if (convData.conversations?.length > 0) {
                targetConvId = targetConvId || convData.conversations[0].conversation_id;
                targetVesselId = targetVesselId || convData.conversations[0].vessel_id;
              }
            }
          } catch (_) { /* network error — just start fresh */ }
        }

        // Redirect to the direct vessel URL with ?conversation= so we land
        // on the exact conversation_id the steward is chatting on.
        setTimeout(() => {
          if (cancelled) return;
          let target = '/vessels';
          if (targetVesselId && targetConvId) {
            target = `/vessel/${targetVesselId}?conversation=${targetConvId}`;
          } else if (targetVesselId) {
            target = `/vessel/${targetVesselId}`;
          }
          navigate(target, { replace: true });
        }, 1500);
      } catch (e) {
        if (cancelled) return;
        setStatus('error');
        setErrorMsg(e.__detail || e.message || 'Network error');
      }
    })();

    return () => { cancelled = true; };
  }, [token, navigate]);
  
  return (
    <div className="relative min-h-screen flex items-center justify-center px-4">
      <CosmicBackground />
      <div className="glass rounded-2xl p-8 max-w-md w-full text-center relative z-10" data-testid="agent-guest-join">
        {status === 'joining' && (
          <>
            <Loader2 className="w-8 h-8 animate-spin text-etheric mx-auto mb-4" />
            <h1 className="text-starlight text-xl font-heading mb-2">Opening the vessel gate</h1>
            <p className="text-starlight/50 text-sm">Verifying your invitation…</p>
          </>
        )}
        {status === 'ok' && (
          <>
            <Sparkles className="w-8 h-8 text-etheric mx-auto mb-4" />
            <h1 className="text-starlight text-xl font-heading mb-2">Welcome, {guestName}</h1>
            <p className="text-starlight/60 text-sm">
              You've been received into the vessel field. Taking you to the vessels now…
            </p>
          </>
        )}
        {status === 'error' && (
          <>
            <AlertTriangle className="w-8 h-8 text-red-300 mx-auto mb-4" />
            <h1 className="text-starlight text-xl font-heading mb-2">The gate did not open</h1>
            <p className="text-starlight/60 text-sm mb-4">{errorMsg}</p>
            <p className="text-starlight/40 text-xs">
              Ask the steward for a fresh invite link.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
